import { z } from 'zod';
import {
  dateInZone,
  instantMilliseconds,
  parsePlainDate,
  previousBusinessDay,
} from '../dates/index.js';
import { failure, success, type PlainDate, type Result } from '../types.js';
import { isNotificationEvent } from './catalog.js';

const id = z.string().trim().min(1).max(200);
const date = z.string().refine((value) => parsePlainDate(value).ok);
const instant = z.union([z.number(), z.string()]).refine((value) => instantMilliseconds(value).ok);
const windowSchema = z
  .object({
    opensOn: date,
    deadlineDate: date,
    opensAt: z.number().int(),
    deadlineAt: z.number().int(),
    reminderDates: z.array(date),
  })
  .strict();
const reminderSchema = z
  .object({
    now: instant,
    timeZone: z.string(),
    window: windowSchema,
    windowRevision: id,
    lineId: id,
    personId: id,
    state: z.enum([
      'S0',
      'S1a',
      'S1b',
      'S2',
      'S3',
      'S4',
      'S5',
      'S6',
      'S7',
      'S7x',
      'S8',
      'S9',
      'S10',
      'S11',
      'X1',
      'X2',
    ]),
    elected: z.boolean(),
    removed: z.boolean(),
    visible: z.boolean(),
    previewing: z.boolean().optional(),
  })
  .strict();
export interface ElectionReminderOccurrence {
  readonly event: 'election_reminder';
  readonly lineId: string;
  readonly personId: string;
  readonly scheduledOn: PlainDate;
  readonly deadlineDate: string;
  readonly occurrenceId: string;
}

/**
 * Use the current saved electionWindow output and revision. M6 dates already account for weekends.
 * APP-031: send on that local date only; a missed earlier day does not create a burst of stale mail.
 * Retry workers must call again with current visibility/election/state facts before delivery.
 */
export function dueElectionReminder(input: unknown): Result<ElectionReminderOccurrence | null> {
  const parsed = reminderSchema.safeParse(input);
  if (!parsed.success) return failure('invalid_reminder_facts');
  const f = parsed.data;
  const now = instantMilliseconds(f.now);
  if (!now.ok) return now;
  const today = dateInZone(f.now, f.timeZone);
  if (!today.ok) return today;
  const w = f.window;
  const opensOn = dateInZone(w.opensAt, f.timeZone);
  const deadlineDate = dateInZone(w.deadlineAt, f.timeZone);
  if (
    !opensOn.ok ||
    !deadlineDate.ok ||
    opensOn.value !== w.opensOn ||
    deadlineDate.value !== w.deadlineDate ||
    w.deadlineAt < w.opensAt
  )
    return failure('invalid_reminder_window');
  for (const scheduled of w.reminderDates) {
    const weekday = previousBusinessDay(scheduled);
    if (
      !weekday.ok ||
      weekday.value !== scheduled ||
      scheduled < w.opensOn ||
      scheduled > w.deadlineDate
    )
      return failure('invalid_reminder_schedule');
  }
  if (
    f.previewing ||
    f.elected ||
    f.removed ||
    !f.visible ||
    f.state !== 'S7' ||
    now.value < w.opensAt ||
    now.value > w.deadlineAt ||
    !w.reminderDates.includes(today.value)
  )
    return success(null);
  return success({
    event: 'election_reminder',
    lineId: f.lineId,
    personId: f.personId,
    scheduledOn: today.value,
    deadlineDate: w.deadlineDate,
    occurrenceId: JSON.stringify([f.windowRevision, w.deadlineDate, today.value]),
  });
}

const deliverySchema = z
  .object({
    event: z.string().refine(isNotificationEvent),
    occurrenceId: z.string().min(1).max(1000),
    scopeId: id,
    target: z
      .object({
        kind: z.enum(['person', 'group']),
        id,
        view: z.enum(['employee', 'staff', 'personal']),
      })
      .strict(),
    channel: z.enum(['email', 'in_app']),
  })
  .strict();

/** Persist under a unique constraint or queue singleton key in the mutation transaction. */
export function notificationDeliveryKey(input: unknown): Result<string> {
  const parsed = deliverySchema.safeParse(input);
  if (!parsed.success) return failure('invalid_notification_delivery');
  const d = parsed.data;
  return success(
    JSON.stringify([
      'notification-v1',
      d.event,
      d.scopeId,
      d.occurrenceId,
      d.target.kind,
      d.target.id,
      d.target.view,
      d.channel,
    ]),
  );
}
